import React, { createContext, useContext, useState, useEffect, useCallback, useRef, startTransition } from 'react';
import keycloak, { identityReachable } from '../services/keycloak';
import { useSignInIntro, hasAuthResponse } from 'shared/signin/signInIntro';
import { apiClient } from '../services/apiClient';
import { getMe } from '../services/authService';
import { beginSignOut, isSignedOutPath, signedOutUrl, signInAgainOptions } from 'shared/signin/signOut';
import { resetRedirects } from 'shared/signin/signInRedirect';



const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

/** Minimum token validity (seconds) before proactive refresh. */
const MIN_VALIDITY_SECONDS = 60;

/** How often to check token expiry and refresh proactively (ms). */
const REFRESH_INTERVAL_MS = 30_000;

/** Allowed Keycloak roles for the provider plane. */
const ALLOWED_ROLES = ['admin', 'provider_admin'];

const KEYCLOAK_REDIRECT_URI = process.env.REACT_APP_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/sso`;

/**
 * Share the initialization promise across mounts. StrictMode can reattach to
 * the same authentication result without calling keycloak.init() twice.
 */
let _kcInitPromise;

const PERMISSIONS_INTERVAL_MS = 120_000;
const PERMISSIONS_TIMEOUT_MS = 8_000;

// Abort invalidates results even when the underlying HTTP call cannot cancel.
function boundedRequest(operation, signal) {
  return new Promise((resolve, reject) => {
    let timer;
    const finish = (callback, value) => { clearTimeout(timer); signal.removeEventListener('abort', abort); callback(value); };
    const abort = () => finish(reject, new Error('Session ended'));
    if (signal.aborted) { reject(new Error('Session ended')); return; }
    signal.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => finish(reject, new Error('Permissions request timed out')), PERMISSIONS_TIMEOUT_MS);
    Promise.resolve().then(() => { if (signal.aborted) throw new Error('Session ended'); return operation(); })
      .then((value) => finish(resolve, value), (error) => finish(reject, error));
  });
}

function retryDelay(ms, signal) {
  return new Promise((resolve) => {
    const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, ms);
    if (signal.aborted) finish();
    else signal.addEventListener('abort', finish, { once: true });
  });
}

function buildUser(kc) {
  const parsed = kc.tokenParsed || {};
  const realmRoles = parsed.realm_access?.roles || [];
  const clientRoles = parsed.resource_access?.[kc.clientId]?.roles || [];
  const allRoles = [...realmRoles, ...clientRoles];
  const role = ALLOWED_ROLES.find((r) => allRoles.includes(r)) || allRoles[0] || 'unknown';
  return {
    id:       parsed.sub,
    username: parsed.preferred_username || parsed.email || parsed.sub,
    email:    parsed.email,
    name:     parsed.name,
    roles:    allRoles,
    role,
  };
}

function syncToken(token) {
  if (token) {
    localStorage.setItem('infinity_token', token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('infinity_token');
    delete apiClient.defaults.headers.common['Authorization'];
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // The start-up intro, when there is one: login-required sends the page to the sign-in page, so
  // the intro fades its content out first (unless the URL already carries the answer).
  const intro = useSignInIntro();
  const leaveForRedirect = intro && intro.active ? intro.leaveForRedirect : null;
  const leaveRef = useRef(leaveForRedirect);
  leaveRef.current = leaveForRedirect;
  // The identity server could not be reached at start-up: Login shows its branded error state.
  const [identityUnavailable, setIdentityUnavailable] = useState(false);
  const [permissionsStatus, setPermissionsStatus] = useState('idle');
  const [permissionsError, setPermissionsError] = useState('');
  const lifetime = useRef(null);
  const authenticated = useRef(false);
  const pendingPermissions = useRef(null);
  const lastPermissionsCheck = useRef(0);
  const validatedPermissions = useRef(false);

  const refreshPermissions = useCallback((attempts = 3) => {
    const controller = lifetime.current;
    if (!controller || controller.signal.aborted || !authenticated.current) return Promise.resolve();
    if (pendingPermissions.current?.controller === controller) return pendingPermissions.current.promise;
    const current = () => lifetime.current === controller && !controller.signal.aborted && authenticated.current;
    const request = { controller };
    setPermissionsStatus((status) => status === 'ready' ? status : 'loading');
    setPermissionsError('');
    request.promise = (async () => {
      for (let attempt = 0; attempt < attempts && current(); attempt += 1) {
        lastPermissionsCheck.current = Date.now();
        try {
          const me = await boundedRequest(() => getMe(), controller.signal);
          if (!current()) return;
          if (!me || !Array.isArray(me.permissions) || !me.permissions.every((permission) => typeof permission === 'string' && permission.length > 0)) {
            const invalid = new Error('Invalid permissions response'); invalid.invalidPermissions = true; throw invalid;
          }
          setUser((previous) => previous ? { ...previous, ...me } : previous);
          validatedPermissions.current = true;
          setPermissionsStatus('ready'); setPermissionsError(''); return;
        } catch (error) {
          if (!current()) return;
          const status = error.response?.status;
          const transient = !error.invalidPermissions && (!status || status >= 500 || status === 408 || status === 429);
          if (transient && attempt + 1 < attempts) { await retryDelay((attempt + 1) * 1000, controller.signal); continue; }
          if (transient && validatedPermissions.current) {
            setPermissionsStatus('ready');
            setPermissionsError('Access could not be refreshed. Showing your last verified permissions while the backend is unavailable. Retry to check for changes.');
            return;
          }
          validatedPermissions.current = false;
          setPermissionsStatus('error');
          setPermissionsError(status === 403
            ? 'Your account could not access Infinity permissions. Contact your administrator or retry.'
            : status === 401
              ? 'Infinity could not validate your sign-in session. Sign out and sign in again, then retry if the problem continues.'
            : error.invalidPermissions
              ? 'Infinity returned an incomplete permissions response. Retry to load your access.'
              : 'Infinity could not load your permissions. The backend may be temporarily unavailable. Retry to restore your workspace.');
          return;
        }
      }
    })().finally(() => { if (pendingPermissions.current === request) pendingPermissions.current = null; });
    pendingPermissions.current = request;
    return request.promise;
  }, []);

  const endLocalSession = useCallback(() => {
    authenticated.current = false; validatedPermissions.current = false; lifetime.current?.abort(); pendingPermissions.current = null;
    setToken(null); setUser(null); setPermissionsStatus('idle'); setPermissionsError(''); syncToken(null);
  }, []);
  // Ends the identity session (keycloak-js adds id_token_hint) and lands on /signed-out, which never signs in by itself.
  // endLocalSession runs in the same transition as the router's move to /signed-out, so the sign-in screen never renders first.
  const logout = useCallback(() => { beginSignOut(); startTransition(endLocalSession); keycloak.logout({ redirectUri: signedOutUrl() }); }, [endLocalSession]);
  /** "Sign in again" on the signed-out page: always the credential step, never a silent sign-in. */
  const signInAgain = useCallback(() => {
    resetRedirects();
    return Promise.resolve()
      .then(() => keycloak.login(signInAgainOptions()))
      .catch((error) => console.error('[Keycloak] Sign-in redirect failed:', error));
  }, []);
  const restartLogin = useCallback(() => { endLocalSession(); keycloak.login({ redirectUri: KEYCLOAK_REDIRECT_URI }); }, [endLocalSession]);

  const refreshToken = useCallback(async () => {
    const controller = lifetime.current;
    if (!controller || controller.signal.aborted || !authenticated.current) return;
    try {
      const refreshed = await keycloak.updateToken(MIN_VALIDITY_SECONDS);
      if (lifetime.current !== controller || controller.signal.aborted || !authenticated.current) return;
      if (refreshed) { syncToken(keycloak.token); setToken(keycloak.token); await refreshPermissions(1); }
    } catch {
      if (lifetime.current === controller && !controller.signal.aborted) restartLogin();
    }
  }, [refreshPermissions, restartLogin]);

  useEffect(() => {
    const controller = new AbortController(); lifetime.current = controller; authenticated.current = false; validatedPermissions.current = false;
    const current = () => lifetime.current === controller && !controller.signal.aborted;
    const expired = () => refreshToken();
    // Includes refreshes initiated by the API interceptor.
    const refreshed = () => {
      if (!current() || !authenticated.current) return;
      syncToken(keycloak.token); setToken(keycloak.token); refreshPermissions(1);
    };
    keycloak.onTokenExpired = expired; keycloak.onAuthRefreshSuccess = refreshed;
    // login-required sends the whole page to the identity server: check it answers first.
    // The signed-out page must not sign the visitor back in, so it starts the client without onLoad.
    const signedOut = isSignedOutPath(window.location.pathname);
    if (!_kcInitPromise) _kcInitPromise = Promise.resolve(signedOut || identityReachable()).then((reachable) => {
      if (!reachable) throw new Error('Identity server unreachable');
      return leaveRef.current && !hasAuthResponse() ? leaveRef.current() : undefined;
    }).then(() => {
      return keycloak.init({
        onLoad: signedOut ? undefined : 'login-required', checkLoginIframe: false, pkceMethod: 'S256', redirectUri: KEYCLOAK_REDIRECT_URI,
      });
    });
    _kcInitPromise.then((signedIn) => {
      if (!current()) return;
      if (signedIn && keycloak.token) {
        authenticated.current = true; syncToken(keycloak.token); setToken(keycloak.token); setUser(buildUser(keycloak)); refreshPermissions();
      }
      setLoading(false);
    }).catch((error) => {
      if (!current()) return;
      console.error('[Keycloak] Initialisation failed:', error);
      setIdentityUnavailable(true); setLoading(false);
    });
    const recover = () => {
      if (current() && authenticated.current && Date.now() - lastPermissionsCheck.current >= 10_000) refreshPermissions(1);
    };
    const tokenInterval = setInterval(refreshToken, REFRESH_INTERVAL_MS);
    const permissionsInterval = setInterval(() => refreshPermissions(1), PERMISSIONS_INTERVAL_MS);
    window.addEventListener('online', recover); window.addEventListener('focus', recover);
    return () => {
      controller.abort(); clearInterval(tokenInterval); clearInterval(permissionsInterval);
      window.removeEventListener('online', recover); window.removeEventListener('focus', recover);
      if (keycloak.onTokenExpired === expired) keycloak.onTokenExpired = undefined;
      if (keycloak.onAuthRefreshSuccess === refreshed) keycloak.onAuthRefreshSuccess = undefined;
    };
  }, [refreshPermissions, refreshToken]);

  const login = useCallback(() => keycloak.login(), []);
  /** "Manage account": the identity server's account page, in the realm's own theme. */
  const manageAccount = useCallback(() => keycloak.accountManagement().catch((error) => console.error('[Keycloak] Account page failed:', error)), []);
  // Only validated effective permissions from the backend grant UI access.
  const hasPermission = useCallback((key) => permissionsStatus === 'ready' && Boolean(user?.permissions?.includes('*') || user?.permissions?.includes(key)), [user, permissionsStatus]);
  return <AuthContext.Provider value={{ user, token, loading, login, logout, signInAgain, manageAccount, identityUnavailable, isAuthenticated: !!user,
    permissions: permissionsStatus === 'ready' ? user?.permissions || [] : [], hasPermission, keycloak,
    permissionsStatus, permissionsError, retryPermissions: () => refreshPermissions(),
  }}>{children}</AuthContext.Provider>;
};
